import React, { memo } from "react";
import { Link } from "react-router-dom";
import Agent from "../../Network/Agent";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as NotificationIcon } from "../../assets/svg/notification.svg";
import { Notify_FETCH_SUCCESS } from "../../Reducer/notifyReducer";
import { useTranslation } from "react-i18next";

// Strip markdown / HTML tags so the notification preview is clean plain text
const stripToPlainText = (text) => {
    if (!text) return '';
    return text
        // 1. Convert <br /> to real newlines FIRST so blockquote stripping works per-line
        .replace(/<br\s*\/?>/gi, '\n')
        // 2. Strip inline HTML
        .replace(/<[^>]+>/g, '')
        // 3. Strip markdown formatting
        .replace(/\*\*(.*?)\*\*/gs, '$1')    // **bold**
        .replace(/\*(.*?)\*/gs, '$1')         // *italic*
        .replace(/~~(.*?)~~/gs, '$1')         // ~~strikethrough~~
        .replace(/`{1,3}([\s\S]*?)`{1,3}/g, '$1') // `code` or ```code```
        // 4. Strip blockquote markers at the start of each line
        .replace(/^>\s?/gm, '')
        // 5. Strip any remaining lone > characters
        .replace(/>/g, '')
        // 6. Strip any unmatched markdown syntax chars (e.g. *t... where closing * was truncated)
        .replace(/[*_`~#]/g, '')
        // 7. Collapse whitespace & newlines to single spaces
        .replace(/[\n\r]+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
};

// Map notification type to icon & colour (Facebook-style overlays)
const typeIcon = (type) => {
    switch (Number(type)) {
        case 1: return { icon: 'fa-solid fa-heart',        bg: '#e0245e' }; // love
        case 2: return { icon: 'fa-solid fa-comment',      bg: '#1877f2' }; // comment
        case 3: return { icon: 'fa-solid fa-share',        bg: '#1877f2' }; // share
        case 4: return { icon: 'fa-solid fa-user-plus',    bg: '#42b72a' }; // follow
        case 5: return { icon: 'fa-solid fa-reply',        bg: '#1877f2' }; // reply
        case 7: return { icon: 'fa-solid fa-image',        bg: '#ff9900' }; // story
        default: return { icon: 'fa-solid fa-bell',        bg: '#1877f2' };
    }
};

const notifUrl = (value) => {
    if (value.type == 4) return `/contact/${value.app_url}`;
    if (value.type == 0 || value.type == 6) return `/meeting-details/${value.app_url}`;
    if (value.type == 7) return `/stories/${value.app_url}`;
    // audio_room: DB stores as type='audio_room', Soketi broadcast uses type1='audio_room'
    // app_url carries the room id — deep-link straight into the room
    if (value.type === 'audio_room' || value.type1 === 'audio_room') {
        return value.app_url ? `/audio-rooms/join/${value.app_url}` : `/audio-rooms`;
    }
    return `/post/${value.app_url}`;
};

const Notification = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const notificationData = useSelector((state) => state.notifyReducer?.data);

    const handleReadAll = () => {
        Agent.Notification.readAll();
        if (notificationData) {
            dispatch(Notify_FETCH_SUCCESS({
                ...notificationData,
                total_count: 0,
                notification: notificationData.notification?.map(n => ({
                    ...n,
                    read_at: n.read_at || new Date().toISOString(),
                })),
            }));
        }
    };

    const unreadCount = notificationData?.total_count ?? 0;

    return (
        <div>
            <div className="option-item">
                <div className="dropdown notifications-nav-item">
                    {/* Bell icon trigger */}
                    <Link
                        to={''}
                        onClick={handleReadAll}
                        className="dropdown-bs-toggle notif-bell-btn"
                        role="button"
                        data-bs-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        <div className="notifications-count">
                            <NotificationIcon />
                            {unreadCount > 0 && (
                                <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                            )}
                        </div>
                    </Link>

                    {/* Dropdown panel */}
                    <div className="dropdown-menu notif-panel">
                        {/* Header */}
                        <div className="notif-panel-header">
                            <h2 className="notif-panel-title">{t('Notifications')}</h2>
                            <button
                                type="button"
                                onClick={handleReadAll}
                                className="notif-read-all-btn"
                                title={t('Mark all as read')}
                            >
                                {t('Mark all as read')}
                            </button>
                        </div>

                        {/* Unread / All tabs label */}
                        {unreadCount > 0 && (
                            <div className="notif-section-label">{t('New')}</div>
                        )}

                        {/* Notification list */}
                        <div className="notif-list">
                            {notificationData?.notification?.length === 0 && (
                                <div className="notif-empty">
                                    <i className="fa-regular fa-bell-slash" />
                                    <p>{t('No notifications yet')}</p>
                                </div>
                            )}

                            {notificationData?.notification?.map((value) => {
                                const isUnread = !value.read_at;
                                const { icon, bg } = typeIcon(value.type);
                                const preview = stripToPlainText(value.details);
                                const url = notifUrl(value);

                                return (
                                    <a
                                        key={value.id}
                                        href={url}
                                        className={`notif-item ${isUnread ? 'notif-unread' : ''}`}
                                    >
                                        {/* Avatar + type icon overlay */}
                                        <div className="notif-avatar-wrap">
                                            <div
                                                className="notif-avatar"
                                                style={{ backgroundImage: `url("${value.avatar}")` }}
                                            />
                                            <span className="notif-type-icon" style={{ background: bg }}>
                                                <i className={icon} />
                                            </span>
                                        </div>

                                        {/* Content */}
                                        <div className="notif-content">
                                            <p className="notif-message">
                                                <strong>{value.title}</strong>
                                            </p>
                                            {preview && (
                                                <p className="notif-preview">
                                                    {preview.length > 60 ? preview.slice(0, 60) + '…' : preview}
                                                </p>
                                            )}
                                            <span className="notif-time">{value.times}</span>
                                        </div>

                                        {/* Unread blue dot */}
                                        {isUnread && <span className="notif-dot" />}
                                    </a>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="notif-panel-footer">
                            <Link to="/notifications" className="notif-see-all">
                                {t('See all notifications')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(Notification);